import {
  Component,
  ViewChildren,
  QueryList,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnInit,
  AfterViewInit,
  inject,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { HistoryItem, Sensor, UiService, UserSettings } from '@lp-ui-lib';
import { I18nService } from '@lp-ui-lib';
import { SettingsService } from '@lp-ui-lib';
import { StorageService } from '@lp-ui-lib';
import { AssetDetail } from '@lp-ui-lib';
import { CurrentTelemetry } from '@lp-ui-lib';
import { TelemetryHistory } from '@lp-ui-lib';
import { GraphData } from '@lp-ui-lib';
import { Location } from '@lp-ui-lib';
import { environment } from '../../../../environments/environment';
import { HeaderComponent } from '@lp-ui-lib';
import { SpinnerComponent } from '@lp-ui-lib';
import { SidebarComponent } from '@lp-ui-lib';
import { NotificationAlertComponent } from '../../../components/notification-alert/notification-alert';
import { BreadcrumbsComponent } from '@lp-ui-lib';
import { HistoryItemComponent } from '../../../components/history-item/history-item';
import { NzListModule } from 'ng-zorro-antd/list';
import { NavButtonComponent } from '@lp-ui-lib';
import { DocumentationWizardComponent } from '../../../components/documentation-wizard/documentation-wizard';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { GraphComponent } from '../../../components/graph/graph';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { FormsModule } from '@angular/forms';
import { TabBarItemComponent } from '../../../components/tab-bar/tab-bar-item/tab-bar-item';
import { SensorValueComponent } from '../../../components/sensor-value/sensor-value';
import { TabBarComponent } from '../../../components/tab-bar/tab-bar';
import { AssetsService, isValidDate } from '@lp-ui-lib';

@Component({
  selector: 'app-telemetry-page',
  imports: [
    HeaderComponent,
    SpinnerComponent,
    SidebarComponent,
    NotificationAlertComponent,
    BreadcrumbsComponent,
    HistoryItemComponent,
    NzListModule,
    NavButtonComponent,
    FormsModule,
    DocumentationWizardComponent,
    NzDescriptionsModule,
    NzIconModule,
    DatePipe,
    NzDatePickerModule,
    TabBarItemComponent,
    SensorValueComponent,
    TabBarComponent,
    GraphComponent,
  ],
  templateUrl: './telemetry-page.html',
  styleUrls: ['./telemetry-page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TelemetryPage implements OnInit, AfterViewInit {
  public asset: AssetDetail | any = null;
  public asset4Sidebar: AssetDetail | any = null; // used so that the sidebar does not 'lose' the measurements which are filtered out in this view
  public sensor: Sensor | any = null;
  public telemetry: CurrentTelemetry | any = null;
  public telemetry_history: TelemetryHistory | any = null;
  public isOfEdgeDevice = false;
  public telemetry_graphData: GraphData[] = [];
  public loading = false;
  public telemetry_loading = false;
  public error = false;
  public thumbnailLoading: any;
  public breadcrumbsLoading: any;
  public overlayDocumentation = false;
  public historyItems: HistoryItem[] = [];
  public documentationAdded = false;
  public assetID: any;
  public sensorID: any;
  public measurementID: any;
  public timeframe: any = null;
  public loadedTimeframe: any = null;
  public settings: UserSettings | any;
  private lastTimeframeSelectIndex = 0;
  public plantID = -1;
  public edgeDeviceID = '';

  @ViewChildren('datePick') datePickers: QueryList<any> | any;

  public range: Date[] = [new Date(new Date().setDate(new Date().getDate() - 1)), new Date()];

  get isMobile(): boolean {
    return this.ui.IsMobile();
  }
  get isTablet(): boolean {
    return this.ui.IsTablet();
  }
  get isDesktop(): boolean {
    return this.ui.IsDesktop();
  }
  get Title(): string {
    return this.i18n.parse(this.telemetry.title);
  }

  get IsTilt(): boolean {
    return this.IsSensorHealth && this.telemetry.measurementTypeID === 1034;
  }

  get IsBattery(): boolean {
    return this.IsSensorHealth && this.telemetry.measurementTypeID === 1018;
  }

  get IsSignalQuality(): boolean {
    return this.IsSensorHealth && this.telemetry.measurementTypeID === 1028;
  }

  get DisplayErrorCard(): boolean {
    return (
      (this.IsTilt || this.IsBattery || this.IsSignalQuality) &&
      (this.telemetry.critical || this.telemetry.warning)
    );
  }

  get ErrorCardText(): string {
    if (this.IsTilt && this.telemetry.critical) {
      return this.i18n.langString('tilt_text');
    } else if (this.IsTilt && this.telemetry.warning && !this.telemetry.critical) {
      return this.i18n.langString('tilt_text_warning');
    } else if (this.IsBattery) {
      return this.i18n.langString('low_battery_text');
    } else if (this.IsSignalQuality) {
      return this.i18n.langString('low_signal_text');
    } else {
      return this.i18n.langString('problem_detected_text');
    }
  }

  get ErrorCardHasReplaceButton(): boolean {
    return this.DisplayErrorCard && !this.IsTilt;
  }
  get InstallationAgentURL(): string {
    return (
      environment.installation_agent_url +
      '/login/refauth/' +
      encodeURIComponent(this.storage.token()) +
      '/' +
      encodeURIComponent(
        btoa(
          JSON.stringify({
            targetUrl: 'plant/edge-device/' + this.plantID + '/' + this.edgeDeviceID,
          })
        )
      )
    );
  }
  get Timeframe(): any {
    return this.timeframe;
  }

  set Timeframe(timeframe: any) {
    if (
      !timeframe ||
      !timeframe.start ||
      !timeframe.end ||
      !isValidDate(timeframe.start) ||
      !isValidDate(timeframe.end)
    )
      timeframe = { start: new Date(), end: new Date(), custom: false };
    this.timeframe = timeframe;
    let reloadData = false;
    if (this.error) reloadData = true;
    if (!this.loadedTimeframe) {
      this.loadedTimeframe = timeframe;
      reloadData = true;
    }
    if (timeframe?.start < this.loadedTimeframe?.start) {
      this.loadedTimeframe.start = timeframe?.start;
      reloadData = true;
    }
    if (timeframe?.end > this.loadedTimeframe?.end) {
      this.loadedTimeframe.end = timeframe?.end;
      reloadData = true;
    }
    if (reloadData) this.refresh();
    this.changeRef.detectChanges();
  }

  get Breadcrumbs(): Location[] {
    return [...this.asset.breadcrumbs, this.asset];
  }

  get IsSensorHealth(): boolean {
    return this.isOfEdgeDevice && !this.telemetry.title.toLowerCase().includes('temperature');
  }

  get Range(): Date[] {
    return this.range;
  }

  set Range(range: Date[]) {
    this.range = range;
  }

  private assetsService = inject(AssetsService);
  private route = inject(ActivatedRoute);
  public router = inject(Router);
  public ui = inject(UiService);
  public changeRef = inject(ChangeDetectorRef);
  public i18n = inject(I18nService);
  public settingsService = inject(SettingsService);
  public storage = inject(StorageService);

  ngOnInit() {
    this.route.paramMap.subscribe((result: any) => {
      this.assetID = +result.params['id'];
      this.sensorID = +result.params['sensorid'];
      this.measurementID = +result.params['measurementid'];
      this.refresh(true);
    });

    this.ui.onNavBack(() => {
      this.goBack();
    });

    // this.ui.OrientaionChange.subscribe(() => {
    //   this.changeRef.detectChanges();
    // });
  }

  ngAfterViewInit(): void {
    this.ui.updateTabIndex(this.route.snapshot.data['tabIndex']);
    this.ui.updateBackBtn(this.route.snapshot.data['backBtn']);
  }

  get VisibleTelemetryGraphs(): any[] {
    return this.telemetry_graphData.filter((data: GraphData) => data.metadata.displayGraph);
  }

  refresh(disableCache = false) {
    this.documentationAdded = false;
    this.changeRef.detectChanges();
    if (!this.telemetry_graphData.length || disableCache) {
      // this only applies when initially loading page, not when reloading more data
      this.loading = true;
      this.changeRef.detectChanges();

      this.settingsService.getUserSettings().then((settings: any) => {
        this.settings = settings;

        this.assetsService
          .getAssetEM(this.assetID, disableCache, settings.MeasurementSystem)
          .then((result1: any) => {
            this.assetsService
              .getSensorsEM(this.assetID, disableCache, settings.MeasurementSystem)
              .then((result2: any) => {
                // todo: we have way too many getNotifications calls at this point. need to develop a better solution,
                //       maybe utilize offline caching for notficiations in assetsService better
                this.assetsService.getNotifications().then((notifications: any) => {
                  result1.asset.notifications = notifications.filter(
                    (notification: any) => notification.objectID === result1.asset.objectID
                  );
                  result1.asset.sensors = result2.sensors;
                  result1.asset.health = result2.health;
                  result1.asset.steamTrap = result2.steamtrap;
                  result1.asset.temperature = result2.temperature;

                  this.thumbnailLoading = result1.thumbnailLoading;
                  this.breadcrumbsLoading = result1.breadcrumbsLoading;
                  this.asset4Sidebar = Object.assign(
                    new AssetDetail(result1.asset, result1.asset.breadcrumbs),
                    { ...result1.asset }
                  ); // copy
                  this.asset = Object.assign(
                    new AssetDetail(result1.asset, result1.asset.breadcrumbs),
                    { ...result1.asset }
                  ); // copy
                  this.asset.notifications = notifications.filter(
                    (noti: any) => noti.objectID === this.asset.objectID
                  );

                  this.ui.setBackText(
                    this.i18n.parse(this.asset.objectName) +
                      (this.IsSensorHealth ? ' | ' + this.i18n.langString('sensor_health') : '')
                  );

                  this.asset.sensors = this.asset.sensors
                    .filter((sensor: any) => sensor.sensorID === this.sensorID)
                    .map((sensor: any) => Object.assign(new Sensor(), sensor)) // copy
                    .map((sensor: any) => {
                      sensor.telemetry = sensor.telemetry.filter(
                        (telemetry: any) => telemetry.measurementTypeID === this.measurementID
                      );
                      return sensor;
                    });
                  this.plantID = result1.plant ? result1.plant.objectID : -1;
                  if (this.asset.sensors.length) {
                    this.sensor = this.asset.sensors.find(
                      (sensor: any) => sensor.sensorID === this.sensorID
                    );
                    this.edgeDeviceID = this.sensor.edgeDigitID;
                    this.telemetry = [
                      ...this.sensor.telemetry,
                      ...this.asset.health,
                      ...(this.asset.temperature ? [this.asset.temperature] : []),
                    ].find((telemetry) => telemetry.measurementTypeID === this.measurementID);
                    if (
                      this.telemetry.title.toLowerCase().includes('temperature') ||
                      this.telemetry.title.toLowerCase().includes('tilt') ||
                      this.telemetry.title.toLowerCase().includes('battery') ||
                      this.telemetry.title.toLowerCase().includes('signal')
                    ) {
                      this.isOfEdgeDevice = true;
                    }
                  }

                  this.changeRef.detectChanges();
                  if (this.loading) {
                    // find default timeframe for loading telemetry (initially)
                    const end =
                      !!this.telemetry &&
                      !!this.telemetry.Timestamp &&
                      isValidDate(this.telemetry.Timestamp)
                        ? this.telemetry.Timestamp
                        : new Date();
                    this.loadedTimeframe = {
                      start: new Date(new Date(end).setDate(end.getDate() - 9)),
                      end: end,
                    };

                    this.changeRef.detectChanges();
                    if (!this.telemetry_loading) {
                      this.telemetry_loading = true;
                      this.assetsService
                        .getTelemetry(
                          this.assetID,
                          this.sensorID,
                          this.measurementID,
                          this.settings.MeasurementSystem,
                          this.loadedTimeframe
                        )
                        .then(
                          (_result: TelemetryHistory) => {
                            this.loading = false;
                            this.telemetry_loading = false;
                            this.telemetry_history = _result;
                            this.telemetry_graphData = this.telemetry_history.GetGraphData();
                            this.onTelemetryTimeframeSelect(0);
                            this.changeRef.detectChanges();
                          },
                          () => {
                            this.loading = false;
                            this.error = true;
                            this.changeRef.detectChanges();
                          }
                        );
                    }
                  }
                });
              });
          });
      });
    } else {
      this.telemetry_graphData = this.telemetry_graphData.map((data) => {
        data.loading = true;
        return data;
      });
      this.changeRef.detectChanges();

      this.settingsService.getUserSettings().then((settings: UserSettings) => {
        this.settings = settings;

        if (!this.telemetry_loading) {
          this.telemetry_loading = true;
          this.changeRef.detectChanges();
          this.assetsService
            .getTelemetry(
              this.assetID,
              this.sensorID,
              this.measurementID,
              this.settings.MeasurementSystem,
              this.loadedTimeframe
            )
            .then((result: TelemetryHistory) => {
              this.telemetry_loading = false;
              this.telemetry_history = result;
              this.telemetry_graphData = this.telemetry_history.GetGraphData();
              this.changeRef.detectChanges();
            });
        } else {
          this.telemetry_graphData = this.telemetry_history.GetGraphData();
          this.changeRef.detectChanges();
        }
      });
    }
  }

  toSensorHistory(sensorID: any, measurementTypeID: any) {
    if (!this.historyItems || this.historyItems.length === 0) return;
    if (sensorID && measurementTypeID) {
      this.router.navigate(['sensor-history', this.asset.objectID, sensorID, measurementTypeID]);
    }
  }

  onDocumentationAdded() {
    this.refresh();
    this.documentationAdded = true;
  }

  isTelemetryTimeframeCustom(): boolean {
    return !!this.timeframe && this.timeframe?.custom;
  }

  onTelemetryTimeframeSelect(index: number) {
    let firstMeasurement: any = null;
    let lastMeasurement: any = null;
    this.lastTimeframeSelectIndex = index;
    if (!this.telemetry_graphData || !this.telemetry_graphData.length) return;
    for (const gdata of this.telemetry_graphData) {
      if (gdata.telemetry.length > 0) {
        if (!gdata.metadata.firstMeasurement) {
          gdata.metadata.lastMeasurement = gdata.telemetry[0].timestamp;
        }

        if (!gdata.metadata.lastMeasurement) {
          gdata.metadata.lastMeasurement = gdata.telemetry[gdata.telemetry.length - 1].timestamp;
        }

        if (!firstMeasurement || gdata.metadata.firstMeasurement < firstMeasurement) {
          firstMeasurement = gdata.metadata.firstMeasurement;
        }

        if (!lastMeasurement || gdata.metadata.lastMeasurement > lastMeasurement) {
          lastMeasurement = gdata.metadata.lastMeasurement;
        }
      }
    }
    if (!firstMeasurement && !lastMeasurement) return;

    if (firstMeasurement) firstMeasurement = new Date(Date.parse(firstMeasurement));
    if (lastMeasurement) lastMeasurement = new Date(Date.parse(lastMeasurement));
    let end: any = lastMeasurement ? lastMeasurement : new Date();
    let start: any = new Date(new Date(end).setDate(end.getDate() - 7)); // default: 7 days
    switch (index) {
      case 0:
        if (start < firstMeasurement) {
          start = firstMeasurement;
          end = new Date(new Date(start).setDate(start.getDate() + 7));
        }
        this.Timeframe = {
          start: start,
          end: end,
          custom: false,
        };
        this.Range = [this.timeframe?.start, this.timeframe?.end];
        break;
      case 1:
        start = new Date(new Date(end).setDate(end.getDate() - 35));
        if (start < firstMeasurement) {
          start = firstMeasurement;
          end = new Date(new Date(start).setDate(start.getDate() + 35));
        }
        this.Timeframe = {
          start: start,
          end: end,
          custom: false,
        };
        this.Range = [this.timeframe?.start, this.timeframe?.end];
        break;
      case 2:
        start = new Date(new Date(end).setMonth(end.getMonth() - 6));
        if (start < firstMeasurement) {
          start = firstMeasurement;
          end = new Date(new Date(start).setMonth(start.getMonth() + 6));
        }
        this.Timeframe = {
          start: start,
          end: end,
          custom: false,
        };
        this.Range = [this.timeframe?.start, this.timeframe?.end];
        break;
    }
    this.changeRef.detectChanges();
  }

  resetTimeframeSelect() {
    this.onTelemetryTimeframeSelect(this.lastTimeframeSelectIndex);
  }

  onRangeStartPick(measurementTypeID: any, sensorID: any, event: any, endPickerID: any, data: any) {
    if (data.openRangePicker === 1) data.openRangePicker = 2;
    data.openRangePicker = 2;
    this.range[0] = event;

    let lastMeasurement = null;

    for (const { metadata } of this.telemetry_graphData) {
      if (!lastMeasurement || metadata.lastMeasurement > lastMeasurement) {
        lastMeasurement = metadata.lastMeasurement;
      }
    }

    if (lastMeasurement) {
      lastMeasurement = new Date(Date.parse(lastMeasurement));
      if (this.range[1] > lastMeasurement) {
        this.range[1] = lastMeasurement;
      }
    }

    // const end: Date = telemetry.Timestamp;
    if (
      this.range[0].toISOString() !== this.timeframe?.start.toISOString() ||
      this.range[1].toISOString() !== this.timeframe?.end.toISOString()
    ) {
      this.Timeframe = {
        start: this.range[0],
        end: this.range[1],
        custom: true,
      };
    }

    if (data.openRangePicker === 2) {
      const el: any = document.getElementById(endPickerID);
      if (el) el.click();
    }
    this.changeRef.detectChanges();
  }

  onRangeEndPick(measurementTypeID: any, sensorID: any, event: any, data: any) {
    data.openRangePicker = 0;
    if (event < this.range[0]) {
      this.range[1] = this.range[0];
      this.range[0] = event;
    } else {
      this.range[1] = event;
    }
    // const end: Date = telemetry.Timestamp;
    if (
      this.range[0].toISOString() !== this.timeframe?.start.toISOString() ||
      this.range[1].toISOString() !== this.timeframe?.end.toISOString()
    ) {
      this.Timeframe = {
        start: this.range[0],
        end: this.range[1],
        custom: true,
      };
    }
    this.changeRef.detectChanges();

    // make sure that the datepicker really closes
    setTimeout(() => {
      data.openRangePicker = 0;
      this.changeRef.detectChanges();
    });
  }

  openRangePicker(id: any, data: any) {
    data.openRangePicker = id.includes('end') ? 2 : 1;
    const el: any = document.getElementById(id);
    if (el) el.click();
  }

  onRangePickerSelect(event: any) {
    this.range = event;
    this.changeRef.detectChanges();
  }

  cancelRangePick(data: any) {
    data.openRangePicker = 0;
    if (this.range[0] > this.range[1]) {
      const temp = this.range[0];
      this.range[0] = this.range[1];
      this.range[1] = temp;
    }
    this.datePickers.forEach((item: any) => item.close());
  }

  rangePickerDisabledDate(data: any, pickerIndex: any): (current: Date) => boolean {
    if (!data) {
      return () => false;
    }
    let otherDate: any = null; // otherDate removes the ability to create empty timespans (in other words, pick the same day twice)
    if (pickerIndex === 1) otherDate = this.range[1];
    if (pickerIndex === 2) otherDate = this.range[0];
    if (otherDate) otherDate = new Date(otherDate.toDateString()); // remove time from datetime object
    let first: Date = new Date(Date.parse(data.metadata.firstMeasurement));
    first = new Date(first.toDateString()); // remove time from datetime object
    let last: Date = new Date(Date.parse(data.metadata.lastMeasurement));
    last = new Date(last.toDateString()); // remove time from datetime object
    const fn = (current: Date) => {
      current = new Date(current.toDateString()); // remove time from datetime object
      return current < first || current > last || +current === +otherDate;
    };
    return fn;
  }

  goBack() {
    if (this.IsSensorHealth) {
      this.router.navigate(['sensor-health', this.asset.objectID]);
    } else {
      this.router.navigate(['asset', this.assetID, -1], { replaceUrl: true });
    }
  }

  noData(data: GraphData) {
    return !data || !data.telemetry.length;
  }
  noDataTelemetry() {
    return !this.VisibleTelemetryGraphs.find((data: GraphData) => !this.noData(data));
  }

  isBreadcrumbClickable() {
    return true;
  }

  navToBreadcrumb(breadcrumb: Location) {
    if (!this.isBreadcrumbClickable()) return;
    if (breadcrumb.isAsset) this.router.navigate(['asset', breadcrumb.objectID, -1]);
    else if (breadcrumb.layerID === 1 || breadcrumb.layerID === 1)
      // is region or country
      this.router.navigate(['home', 'list', breadcrumb.objectID]);
    else this.router.navigate(['plant', breadcrumb.objectID]);
  }
}
